// Shared types for the Hidden Gems Library. Browser-safe.

export type LibraryItem = {
  id: string;
  slug: string;
  extId: string;
  category: string;
  subcategory: string;
  name: string;
  description: string | null;
  url: string;
  domain: string;
  author: string | null;
  sourceType: string | null;
  license: string | null;
  tags: string[];
  whyGem: string | null;
  ogImageUrl: string | null;
  ogStatus: string;
};

// Compact tuple shape used by the search index endpoint / web worker (v2).
// Tuple order: [slug, name, desc, catCode, subCode, tags, author, license, img, addedDay]
//  - catCode / subCode are indexes into the index's `cats` / `subs` tables
//  - img is a bare filename resolved against the index's `imgBase`
//    ("" when the gem has no preview, "!<url>" for an absolute URL)
//  - addedDay is the UTC epoch-day the gem was added (0 when unknown)
// The slug doubles as the row key, so no UUID is shipped to the browser.
export type LibraryIndexRow = [
  string, // slug
  string, // name
  string, // desc
  number, // category code
  number, // subcategory code
  string[], // tags
  string, // author
  string, // license
  string, // img token
  number, // added day (UTC epoch day)
  number, // views
  number, // likes
];

/** Ordering modes supported by the search worker. */
export type SortMode = "relevance" | "name" | "views" | "likes" | "new";

export type LibraryIndex = {
  v: number; // version stamp
  imgBase: string;
  cats: string[];
  subs: string[];
  rows: LibraryIndexRow[];
  categories: { name: string; count: number }[];
};

/** Decoded, render-ready gem used by the grid and the cards. */
export type LibraryGem = {
  slug: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  tags: string[];
  author: string;
  license: string;
  img: string;
  /** UTC epoch day the gem was added; 0 when unknown. */
  addedDay: number;
  views: number;
  likes: number;
};

const DAY_MS = 86_400_000;

/** UTC epoch day for an ISO timestamp — 0 when missing or unparseable. */
export function epochDay(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.floor(t / DAY_MS) : 0;
}

export function todayEpochDay(): number {
  return Math.floor(Date.now() / DAY_MS);
}

/** "today" for same-day drops, "week" for the last 7 days, null otherwise. */
export function freshnessOf(addedDay: number): "today" | "week" | null {
  if (!addedDay) return null;
  const age = todayEpochDay() - addedDay;
  if (age <= 0) return "today";
  if (age <= 7) return "week";
  return null;
}

export function resolveImg(token: string, imgBase: string): string {
  if (!token) return "";
  if (token.startsWith("!")) return token.slice(1);
  return imgBase + token;
}

export function decodeRow(
  row: LibraryIndexRow,
  cats: string[],
  subs: string[],
  imgBase: string,
): LibraryGem {
  return {
    slug: row[0],
    name: row[1],
    description: row[2],
    category: cats[row[3]] ?? "",
    subcategory: subs[row[4]] ?? "",
    tags: row[5],
    author: row[6],
    license: row[7],
    img: resolveImg(row[8], imgBase),
    addedDay: row[9] ?? 0,
    views: row[10] ?? 0,
    likes: row[11] ?? 0,
  };
}

